import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { isGroupRole } from "@custom-types/member-type-guards";
import { Dialog, useToast } from "@flat-ui";
import { createMemberOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { CreateTeamDialogContent } from "@pages/members/teams/create-team-dialog-content";
import { reducerTeams } from "@pages/members/teams/create-team-reducer";
import { useCompanyMembers } from "@hooks/use-company-members";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { setOne } from "@store/members/members-slice";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { convertToTeamMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch } from "@store/store-helper";
import { addMembersToTeam, createTeam } from "@store/teams/teams-slice-thunk";
import { nounPluralize } from "@utils/data-display";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isValidTeamName } from "@utils/team-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useReducer } from "react";
import { MAX_INVITED_MEMBERS } from "@components/teams/teams-constants";
/** The max number of characters for the input name */
export const MAX_NAME_CHARACTERS = 50;

/** The max number of characters for the input description */
export const MAX_DESCRIPTION_CHARACTERS = 500;

/** The dialog for the create team */
export function CreateTeamDialog({
  companyId,
  isDialogOpen,
  onCloseDialog
}) {
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    respondToTeamInvite
  } = useRespondToInvite();
  const {
    openToast
  } = useToast();
  const companyMembers = useCompanyMembers();
  const [state, dispatchTeams] = useReducer(reducerTeams, {
    isLoading: false,
    teamName: "",
    description: ""
  });

  /** Company members reformatted as options for the members autocomplete. */
  const autocompleteOptions = useMemo(() => sortMembersByNameOrEmail(companyMembers).map(member => createMemberOption({
    member
  })), [companyMembers]);
  const {
    selectedMembers,
    selectedValues,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: TEAM_DISPLAY_NAME,
    existingMembers: [],
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_0 => member_0.identity),
    maxInvitedMembers: MAX_INVITED_MEMBERS,
    invalidInputMessage: "Enter a valid email address"
  });

  /** Determines if the team name is valid */
  const isValidName = isValidTeamName(state.teamName);

  /** Whether the selected role is a group-level role */
  const isGroupRoleSelected = !!state.selectedRole && isGroupRole(state.selectedRole);

  /**
   * Whether the create button should be disabled or not.
   * The button should be disabled if:
   * - The team name is empty
   * - The team name isn't valid
   * - The number of selected members exceeds the maximum allowed for invitation.
   *  Note: Selecting members is optional. However, if members are selected, their count must be valid
   *  (i.e., within the allowed range).
   */
  const isCreateDisabled = !isMemberInputValid || uniqueSelectedValues.length > MAX_INVITED_MEMBERS || !isValidName || state.teamName.trim().length < 1 || state.teamName.trim().length > MAX_NAME_CHARACTERS || state.description.trim().length > MAX_DESCRIPTION_CHARACTERS || isGroupRoleSelected && !state.selectedGroupId;
  function resetStates() {
    dispatchTeams({
      type: "SET_TEAM_NAME",
      teamName: ""
    });
    dispatchTeams({
      type: "SET_DESCRIPTION",
      description: ""
    });
    dispatchTeams({
      type: "SET_SELECTED_GROUP_ID",
      selectedGroupId: undefined
    });
    dispatchTeams({
      type: "SET_SELECTED_ROLE",
      selectedRole: undefined
    });
    reset();
  }

  /** Triggered when the create button is clicked, creates the group and closes the dialog */
  async function handleConfirm() {
    dispatchTeams({
      type: "SET_IS_LOADING",
      isLoading: true
    });
    trackEvent({
      name: TeamEvents.createNewTeam,
      props: {
        numberOfMembers: uniqueSelectedValues.length
      }
    });
    try {
      // Create Team
      const team = await dispatch(createTeam({
        companyId,
        teamName: state.teamName.trim(),
        description: state.description.trim()
      })).unwrap();
      const addNewTeamMemberStore = convertToTeamMemberTypes(team, state.selectedRole);
      dispatch(setOne(addNewTeamMemberStore));

      // Assign a role only if a role is selected
      if (state.selectedRole) {
        if (state.selectedGroupId && isGroupRole(state.selectedRole)) {
          // Assign team as a member to a group with group manager or project manager role
          await dispatch(updateMemberRoleInGroup({
            companyId,
            groupId: state.selectedGroupId,
            role: state.selectedRole,
            userIdentity: team.identity
          }));
        } else {
          // Assign member to the company with workspace role
          await dispatch(updateMemberRoleInWorkspace({
            companyId,
            role: state.selectedRole,
            identity: team.identity
          }));
        }
      }
      dispatchTeams({
        type: "SET_TEAM_NAME",
        teamName: state.teamName.trim()
      });

      /** Team creation success message */
      const responseMessage = `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} created successfully`;

      /** Check if there are selected members to add; if so, proceed to add them, otherwise show a success message. */
      if (selectedValues.length > 0) {
        await handleAddMembers(team.id, responseMessage);
      } else {
        openToast({
          title: responseMessage
        });
      }
      onCloseDialog();
      resetStates();
    } finally {
      dispatchTeams({
        type: "SET_IS_LOADING",
        isLoading: false
      });
    }
  }

  /**
   * Add the members to the team
   * @param teamId The id of the team
   * @param responseMessage The message to be displayed after adding the members
   * @returns Promise<void>
   */
  async function handleAddMembers(teamId, responseMessage) {
    if (!companyId) {
      throw new Error("companyId is required");
    }
    try {
      // Add members
      const response = await dispatch(addMembersToTeam({
        companyId,
        teamId,
        members: uniqueSelectedValues
      })).unwrap();
      respondToTeamInvite(response, uniqueSelectedValues.length);
    } catch (error) {
      handleErrorWithToast({
        id: `inviteMemberToTeam-${Date.now().toString()}`,
        title: `${responseMessage}. But couldn't invite the ${nounPluralize({
          shouldShowCounter: false,
          counter: uniqueSelectedValues.length,
          word: "member"
        })}`,
        error
      });
    }
  }
  return <Dialog title={`Create new ${TEAM_DISPLAY_NAME}`} open={isDialogOpen} confirmText="Create" isConfirmDisabled={isCreateDisabled} onConfirm={handleConfirm} showSpinner={state.isLoading} onCancel={() => {
    onCloseDialog();
    resetStates();
  }}>
      <CreateTeamDialogContent state={state} dispatchTeams={dispatchTeams} isGroupRoleSelected={isGroupRoleSelected} selectedMembers={selectedMembers} autocompleteOptions={autocompleteOptions} helperText={helperText} handleMemberSelect={handleMemberSelect} onInputChange={onInputChange} isNewEmailValid={isNewEmailValid} />
    </Dialog>;
}